/**
 * Reading one action's row out of the automation run ledger.
 *
 * Two audiences render the same `results[]` — AutomationRunsModal (one
 * collection's rule) and AdminAutomations (every collection's runs) — so the
 * vocabulary lives here rather than being spelled twice and drifting.
 *
 * WHY THIS MODULE EXISTS AT ALL. A run row used to say "3 written" and
 * nothing else, so a rule that wrote an element and a rule that found it
 * already set produced the same line. The API now names what happened to each
 * element (`result.elements`, API utils/automations/actions/elementOutcomes.js)
 * and this is the half that says it out loud.
 *
 * And a live bug worth naming, because the shape of it recurs: `skipped` is
 * TWO different things. create-item's dedupe reports a COUNT ("4 of these
 * already existed"); update-items and notify report a STRING REASON
 * ('unwritable', 'no-eligible-recipients'). The modal tested it for
 * truthiness and printed `${skipped} already existed`, so a target the rule
 * could not write rendered as "unwritable already existed" — a sentence that
 * is not merely ugly but WRONG about what happened.
 */

// The reasons an action names when it skips a whole target, in words a
// person can act on. An unknown reason is shown verbatim rather than
// swallowed — a new reason should look unfamiliar, not invisible.
const SKIP_REASONS = {
	unwritable: {
		text: 'skipped: not writable',
		title: 'A private or deleted item in the target set — the rule may not write it',
	},
	'no-eligible-recipients': {
		text: 'skipped: nobody to notify',
		title: 'The audience resolved to no one who may receive this',
	},
	read_only: {
		text: 'skipped: managers may only see',
		title: 'An element rule lets managers only see this element, so a rule may not write it; the action was skipped on that record',
	},
};

/**
 * What `result.skipped` means on THIS row.
 * Returns null when there is nothing to say.
 */
export function describeSkipped( skipped, held ) {
	if ( skipped === undefined || skipped === null || skipped === '' || skipped === 0 ) return null;
	if ( typeof skipped === 'number' ) {
		return {
			kind: 'dedupe',
			text: `${ skipped } already existed`,
			title: 'Already created by an earlier run — the natural key matched',
		};
	}
	const reason = `${ skipped }`;
	const heldNames = Array.isArray( held ) ? held.filter( Boolean ) : [];
	if ( reason === 'read_only' && heldNames.length ) {
		return {
			kind: 'reason',
			text: `skipped: managers may only see ${ heldNames.join( ', ' ) }`,
			title: SKIP_REASONS.read_only.title,
		};
	}
	return { kind: 'reason', ...( SKIP_REASONS[ reason ] || { text: `skipped: ${ reason }`, title: reason } ) };
}

// The per-element outcome vocabulary, in the order a reader wants it: what
// moved first, then why the rest did not. Mirrors the API's OUTCOMES list;
// `clamped` and `bound` are reserved there for later actions and are carried
// here so this renderer does not have to change again when they arrive.
const OUTCOME_LABELS = {
	written: 'written',
	unchanged: 'unchanged',
	unknown: 'not on this card',
	driven: 'driven',
	numbered: 'numbered by the server',
	file: 'a file element',
	// A "Me too" count (KPR-390): keepr keeps it from who confirmed.
	counted: 'counts confirmations',
	read_only: 'managers may only see',
	unreadable: 'unreadable',
	too_many_dates: 'more than 1,000 dates',
	clamped: 'clamped',
	bound: 'out of bounds',
};

const OUTCOME_TITLES = {
	written: 'The value moved and the write landed',
	unchanged: 'The value already equalled the stored one — the rule had nothing to do',
	unknown: 'The element does not exist on that target\'s card, so it was filtered',
	driven: 'A system-owned element (driven from a formula or an aggregate) — a rule may not write it',
	numbered: 'The server issues this element\'s number — a rule may not overwrite one, and the number it already holds is not re-issuable',
	file: 'A file element — only a person attaching a file sets one, so a rule leaves it alone',
	counted: 'A Me too count is kept by keepr from the people who confirm — a rule never writes it',
	read_only: 'An element rule lets managers only see this element, so a rule may not write it; the action was skipped on that record',
	unreadable: 'The element could not read that value — a date the rule rendered as free text, say — so it was left alone rather than stored as written',
	too_many_dates: 'A list of dates holds at most 1,000 — the rule\'s list was longer, so it was left alone',
	clamped: 'The value was brought back inside the element\'s bounds',
	bound: 'The value fell outside the element\'s bounds and was refused',
};

// After `file`, as keepr-api's OUTCOMES (elementOutcomes.js) has `counted`.
const OUTCOME_ORDER = [ 'written', 'unchanged', 'unknown', 'driven', 'numbered', 'file', 'counted', 'read_only', 'unreadable', 'too_many_dates', 'clamped', 'bound' ];

/**
 * `result.elements` -> the lines to render, one per outcome that has names.
 * `more` carries what the API's 20-name detail cap cut, so the cap stays
 * visible here too: a list that quietly stopped would undo the point of it.
 */
export function elementOutcomeLines( elements ) {
	if ( ! elements || typeof elements !== 'object' ) return [];
	const more = elements.more && typeof elements.more === 'object' ? elements.more : {};
	return OUTCOME_ORDER
		.map( outcome => {
			const names = Array.isArray( elements[ outcome ] ) ? elements[ outcome ] : [];
			if ( ! names.length ) return null;
			const extra = Number( more[ outcome ] ) || 0;
			return {
				outcome,
				label: OUTCOME_LABELS[ outcome ] || outcome,
				title: OUTCOME_TITLES[ outcome ] || outcome,
				text: `${ OUTCOME_LABELS[ outcome ] || outcome }: ${ names.join( ', ' ) }${ extra ? ` (+${ extra } more)` : '' }`,
			};
		} )
		.filter( Boolean );
}

/**
 * What an update-items action did to TAGS (keepr-api T6a: `addTagIds` /
 * `removeTagIds`, the summary row's `tags: { added, removed }` — tag
 * applications, one per item and tag), as run-row words; null when the
 * action named no tags.
 */
export function describeTagChanges( tags ) {
	if ( ! tags || typeof tags !== 'object' ) return null;
	const added = Number( tags.added ) || 0;
	const removed = Number( tags.removed ) || 0;
	const count = ( n, verb ) => `${ n } ${ n === 1 ? 'tag' : 'tags' } ${ verb }`;
	if ( ! added && ! removed ) {
		return { text: 'no tags changed', title: 'Every item already had the tags this action puts on, and none it takes off' };
	}
	return {
		text: [ added ? count( added, 'added' ) : null, removed ? count( removed, 'removed' ) : null ].filter( Boolean ).join( ', ' ),
		title: 'Tags this action put on items and took off them, counted once per item and tag',
	};
}

/**
 * An automatic tag's own run row (kind `tag-rule`, keepr-api T6a): one
 * result per item it re-checked, `applied` or `removed` when the tag moved.
 */
export function describeTagRuleResult( result ) {
	if ( ! result || result.action !== 'tag-rule' ) return null;
	if ( result.applied ) return { text: 'tag put on', title: 'The rule holds for this item now, so its tag went on' };
	if ( result.removed ) return { text: 'tag taken off', title: 'The rule stopped holding for this item, so its tag came off' };
	return null;
}

/**
 * A rule that watches a repeating group's ENTRIES (keepr-api KPR-687): each
 * per-entry result names its entry (`entry_id`); a `set-entry` result says
 * what became of each entry it was asked to write (`entries: { written?,
 * unchanged?, refused?: [{ id, error }], removed? }`). Short words for the run
 * row, with the ids in the title; null when the row carries neither.
 */
export function describeEntryResult( result ) {
	if ( ! result || typeof result !== 'object' ) return null;
	const entries = result.entries && typeof result.entries === 'object' ? result.entries : null;
	if ( entries ) {
		const count = key => ( Array.isArray( entries[ key ] ) ? entries[ key ].length : 0 );
		const said = [
			count( 'written' ) ? `${ count( 'written' ) } ${ count( 'written' ) === 1 ? 'entry' : 'entries' } written` : null,
			count( 'unchanged' ) ? `${ count( 'unchanged' ) } already so` : null,
			count( 'refused' ) ? `${ count( 'refused' ) } refused` : null,
			count( 'removed' ) ? `${ count( 'removed' ) } removed before the write` : null,
		].filter( Boolean );
		if ( ! said.length ) return null;
		const refused = Array.isArray( entries.refused ) ? entries.refused : [];
		return {
			text: said.join( ', ' ),
			title: [
				...[ 'written', 'unchanged', 'removed' ].filter( key => count( key ) ).map( key => `${ key }: ${ entries[ key ].join( ', ' ) }` ),
				...refused.map( r => `refused ${ r?.id }: ${ r?.error }` ),
			].join( '\n' ),
		};
	}
	if ( result.entry_id ) return { text: `entry ${ result.entry_id }`, title: 'The entry this action ran for' };
	return null;
}

export { SKIP_REASONS, OUTCOME_LABELS, OUTCOME_ORDER };
